import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import disclaimersService from '../api/disclaimersService';
import type {
    Disclaimer,
    CreateDisclaimerDto,
    UpdateDisclaimerDto,
} from '../types/types';

const invalidateDisclaimerLinks = (qc: ReturnType<typeof useQueryClient>) => {
    qc.invalidateQueries({ queryKey: ['disclaimers'] });
    qc.invalidateQueries({ queryKey: ['testConfigurations'] });
    qc.invalidateQueries({ queryKey: ['testMetrics'] });
};

export const useDisclaimers = () => {
    return useQuery<Disclaimer[]>({
        queryKey: ['disclaimers'],
        queryFn: () => disclaimersService.getAll(),
        refetchOnWindowFocus: false,
    });
};

export const useDisclaimer = (id: number | undefined) => {
    return useQuery<Disclaimer>({
        queryKey: ['disclaimer', id],
        queryFn: () => disclaimersService.getById(id!),
        enabled: id != null,
        refetchOnWindowFocus: false,
    });
};

export const useCreateDisclaimer = () => {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (dto: CreateDisclaimerDto) => disclaimersService.create(dto),
        onSuccess: () => invalidateDisclaimerLinks(qc),
    });
};

export const useUpdateDisclaimer = () => {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: ({ id, dto }: { id: number; dto: UpdateDisclaimerDto }) =>
            disclaimersService.update(id, dto),
        onSuccess: (_, { id }) => {
            invalidateDisclaimerLinks(qc);
            qc.invalidateQueries({ queryKey: ['disclaimer', id] });
        },
    });
};

export const useDeleteDisclaimer = () => {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (id: number) => disclaimersService.remove(id),
        onSuccess: () => invalidateDisclaimerLinks(qc),
    });
};
